/**
 * 產品設計（憑證設計＋套裝）的匯出／匯入（批107 項 7）。
 *
 * ## 為什麼獨立一個檔而不是塞進 `api/ls.ts`
 *
 * `ls.ts` 是「後台每一支端點」的大雜燴（一千七百行），而匯出／匯入是一組**自成一格**
 * 的型別：差異結構（`ProductDiff`）只有 `ProductsView` 的匯入預覽在用，放進 ls.ts 只會
 * 讓那個檔更難找東西。端點前綴也是獨立的（`/api/admin/product-io/*`——`export` 是合法的
 * 產品 key，不能放 `/products/` 底下，理由見後端 `api/admin_products.py` 的註解）。
 */
import { api } from './client'

/** 匯出檔的格式識別字串與版本（與後端 `services/product_io.FORMAT`／`VERSION` 同值）。 */
export const PRODUCT_DOC_FORMAT = 'iplusone-ls-products'
export const PRODUCT_DOC_VERSION = 1

/**
 * 匯出檔裡的一個套裝。**沒有 `id`**——那是來源 LS 的列位址，搬過去沒有意義。
 *
 * ★ 批114 對齊後端 `services/product_io.PlanDoc`：批109 之後那幾個數字全部住在
 *   `quotas` 裡，匯出檔**早就沒有**獨立欄位了（這裡的宣告一直是多的，只是沒人讀）。
 */
export interface ProductDocPlan {
  key: string
  label: string
  kind: string
  sort_order: number
  is_active: boolean
  term_days: number | null
  /** 批108：期限的分鐘餘數（缺席＝0＝整天制）。 */
  term_minutes: number
  modules: Record<string, string>
  /** null 與 `{}` 語意不同：null＝這個套裝不設配額。 */
  quotas: Record<string, number | null> | null
  /** 批110：行為參數預設值。null＝舊版匯出檔沒有這個鍵（匯入時不動目標的現值）。 */
  policies?: Record<string, unknown> | null
  activation_policy: string
  trial: boolean
}

/**
 * 匯出檔裡的一個產品。
 *
 * ★ **沒有 `id`、沒有 `signing_kid`、`options` 裡沒有 `supports_hosting`**：三者都是
 *   各環境自己的營運決定，不隨設計搬家。`status` 帶著只是資訊，匯入時不套用。
 * ★ 批141：`options.hosting_profile`（代管設定）**會**隨設計搬家——檔案有這一格就嚴格
 *   驗證後整份取代（驗不過 422 `hosting_profile_invalid`，帶 `product`／`field`）、明寫
 *   `null`＝移除、沒有這一格＝保留目標現值。
 */
export interface ProductDoc {
  key: string
  name: string
  status: string
  notes: string | null
  modules: Record<string, unknown>[]
  quotas: Record<string, unknown>[]
  options: Record<string, unknown>
  plans: ProductDocPlan[]
}

export interface ProductDocument {
  format: string
  version: number
  exported_at?: string | null
  products: ProductDoc[]
}

/** 模組／配額清單的差異（`reordered`＝共同鍵的相對順序變了，順序＝核發表單的欄序）。 */
export interface ListDiff {
  added: string[]
  removed: string[]
  changed: string[]
  reordered: boolean
}

export interface OptionsDiff {
  added: Record<string, unknown>
  removed: Record<string, unknown>
  changed: Record<string, { from: unknown; to: unknown }>
}

export interface PlanUpdateDiff {
  key: string
  label: string
  changes: Record<string, { from: unknown; to: unknown }>
}

export interface PlansDiff {
  created: string[]
  updated: PlanUpdateDiff[]
  /** 目標有、檔案沒有的套裝——**只報不刪**（`licenses.plan` 存的是 key 的快照）。 */
  extra_in_target: string[]
}

/** 單一產品的差異項。`action='forbidden'`＝這個產品不在你的產品範圍內。 */
export interface ProductDiff {
  key: string
  name: string
  action: 'create' | 'update' | 'unchanged' | 'forbidden'
  exists: boolean
  basic: Record<string, { from: unknown; to: unknown }>
  modules: ListDiff
  quotas: ListDiff
  options: OptionsDiff
  plans: PlansDiff
  /**
   * 批141：代管設定（`options.hosting_profile`）的差異——**另立一欄**，不混進 `options`。
   * `change`：`added`｜`changed`｜`removed`（檔案明寫 null）｜`null`（沒變動，含「檔案沒有
   * 這一格」＝保留現值）。`fields`：`changed` 時變動的頂層鍵。舊版後端沒有這一欄。
   */
  hosting_profile?: ProfileDiff
  /**
   * 批151：參數宣告（`options.versioned_params`）的差異——同樣另立一欄。`added`／`removed`／
   * `changed` 是參數鍵；`needs_initial_values`＝檔案新增了這裡既有版本沒有的鍵（移除過又重新宣告
   * 的鍵也算，舊值不沿用；套用會被拒絕，要先到〈產品參數版本〉頁籤帶初始值設定宣告）。
   * 參數版本（值）本身不匯出也不匯入。
   */
  versioned_params?: ParamsDiff
  warnings: string[]
  /** 紅字：刪鍵、開通政策放寬、新套裝政策不是 superadmin、trial／kind 變更。 */
  dangers: string[]
}

export interface ParamsDiff {
  change: 'added' | 'changed' | 'removed' | null
  added: string[]
  removed: string[]
  changed: string[]
  needs_initial_values?: string[]
}

export interface ProfileDiff {
  change: 'added' | 'changed' | 'removed' | null
  fields: string[]
  /**
   * 裁示 48（LS 1.0.62）：出貨 channel 是**這台**授權中心專用的值——只在 `added`，或
   * `changed` 且 `channel` 在 `fields` 裡時出現（`from` 為 null＝目標原本沒有）。不擋，
   * 後端另在 `warnings` 提醒「從別台匯入請確認」。
   */
  channel?: { from: string | null; to: string }
}

export interface ImportResult {
  dry_run: boolean
  format: string
  version: number
  products: ProductDiff[]
  /** 只有 `dry_run=false` 才有：實際套用了哪些（`unchanged` 代表跳過）。 */
  applied?: { key: string; action: string }[]
}

/** 匯出產品設計。`key` 省略＝產品範圍內的全部產品。admin 以上。 */
export const exportProducts = (key?: string) =>
  api<ProductDocument>(
    key ? `/api/admin/product-io/export?key=${encodeURIComponent(key)}` : '/api/admin/product-io/export',
  )

/** 匯入預覽：只算差異，**一個字都不寫**。superadmin。 */
export const previewProductImport = (document: ProductDocument) =>
  api<ImportResult>('/api/admin/product-io/import', {
    method: 'POST',
    body: { document, dry_run: true },
  })

/**
 * 套用匯入：單一交易、**重新算一次差異**（不信任預覽當時的結果）。superadmin。
 * `keys` 必填非空——「整份照單全收」刻意不給，要逐一勾選看過差異的那些產品。
 */
export const applyProductImport = (document: ProductDocument, keys: string[]) =>
  api<ImportResult>('/api/admin/product-io/import', {
    method: 'POST',
    body: { document, dry_run: false, keys },
  })

/** 這份 JSON 看起來像不像我們的匯出檔（選檔當下先擋一次，不必往返伺服器）。 */
export function looksLikeProductDocument(value: unknown): value is ProductDocument {
  if (!value || typeof value !== 'object') return false
  const doc = value as Partial<ProductDocument>
  return doc.format === PRODUCT_DOC_FORMAT && Array.isArray(doc.products)
}

/** 觸發瀏覽器下載（Blob；與 `lib/csvSafe` 的 `downloadCsv` 同一套寫法）。 */
export function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json;charset=utf-8',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** 檔名：`產品設計-<key|all>-YYYYMMDD.json`（同一天重複匯出會覆蓋，那是想要的行為）。 */
export function exportFilename(key?: string): string {
  const d = new Date()
  const stamp = [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('')
  return `product-design-${key || 'all'}-${stamp}.json`
}
